import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Bold, CheckCircle2, ClipboardList, CreditCard, Eye, FileText, FileUp, History, ImagePlus, KeyRound,
  List, Loader2, MoveLeft, MoveRight, Package, PackageCheck, PenLine, Save, Send, Trash2, Truck, User, XCircle,
} from "lucide-react";
import { api, apiError, fileUrl } from "@/lib/api";
import { formatPrice, formatDateTime, PAYMENT_LABELS, ORDER_STATUS, ORDER_STATUS_OPTIONS, PAYMENT_STATUS } from "@/lib/format";
import { RichTextBlocks } from "@/components/RichText";
import LinkInserter from "@/components/admin/LinkInserter";
import WhatsAppLink from "@/components/WhatsAppLink";

const Section = ({ icon: Icon, title, children, action }) => (
  <div className="rounded-2xl border border-line bg-white p-5">
    <div className="mb-3 flex items-center justify-between gap-2">
      <p className="flex items-center gap-2 text-sm font-extrabold text-ink">
        <Icon className="h-4 w-4 text-brand" /> {title}
      </p>
      {action}
    </div>
    {children}
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-3 py-1 text-xs">
    <span className="text-slate-500">{label}</span>
    <span className="text-right font-semibold text-ink">{value || "—"}</span>
  </div>
);

function DeliveryPreview({ order, form }) {
  return (
    <div className="rounded-xl border border-line bg-surface p-4" data-testid="delivery-preview">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-extrabold text-ink">Commande {order.order_number}</p>
        <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-[10px] font-bold text-emerald-700">
          Livrée
        </span>
      </div>
      <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
        <p className="flex items-center gap-1.5 text-xs font-bold text-emerald-800">
          <CheckCircle2 className="h-3.5 w-3.5" /> Votre commande a été livrée
        </p>
      </div>
      {(form.title || form.content || form.images.length > 0 || form.files.length > 0) ? (
        <div className="mt-3 rounded-lg border border-line bg-white p-3.5">
          {form.title && <p className="text-sm font-extrabold text-ink">{form.title}</p>}
          {form.content && (
            <RichTextBlocks text={form.content} className="mt-2 text-xs leading-relaxed text-slate-600" />
          )}
          {form.images.length > 0 && (
            <div className="mt-3 grid grid-cols-3 gap-2">
              {form.images.map((img) => (
                <img key={img} src={fileUrl(img)} alt="" className="h-16 w-full rounded-lg border border-line object-cover" />
              ))}
            </div>
          )}
          {form.files.length > 0 && (
            <div className="mt-3 space-y-1.5">
              {form.files.map((f, i) => (
                <p key={`${f.url}-${i}`} className="flex items-center gap-2 rounded-lg border border-line bg-surface px-2.5 py-1.5 text-[11px] font-semibold text-ink">
                  <FileText className="h-3.5 w-3.5 shrink-0 text-brand" /> <span className="truncate">{f.name}</span>
                </p>
              ))}
            </div>
          )}
        </div>
      ) : (
        <p className="mt-3 rounded-lg border border-dashed border-line p-4 text-center text-[11px] text-slate-400">
          Le contenu que vous rédigez apparaîtra ici.
        </p>
      )}
      <p className="mt-3 rounded-lg bg-brand-soft/60 p-2.5 text-[10.5px] leading-snug text-slate-600">
        ℹ️ Le client voit ce contenu uniquement après « Livrer la commande ».
      </p>
    </div>
  );
}

export default function AdminOrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [form, setForm] = useState({ title: "", content: "", images: [], files: [] });
  const [meta, setMeta] = useState({ status: "pending", tracking_number: "", note: "" });
  const [savingMeta, setSavingMeta] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [delivering, setDelivering] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const contentRef = useRef(null);
  const fileRef = useRef(null);
  const attachRef = useRef(null);
  const [verifyNote, setVerifyNote] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [proofUrl, setProofUrl] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/orders/${id}`);
      setOrder(data);
      const d = data.delivery || {};
      setForm({ title: d.title || "", content: d.content || "", images: d.images || [], files: d.files || [] });
      setMeta({ status: data.status || "pending", tracking_number: data.tracking_number || "", note: "" });
    } catch (error) {
      toast.error(apiError(error));
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const insertAtCursor = (token, block = false) => {
    const el = contentRef.current;
    if (!el) {
      setForm((f) => ({ ...f, content: `${f.content}${block ? "\n" : " "}${token}` }));
      return;
    }
    const start = el.selectionStart ?? form.content.length;
    const end = el.selectionEnd ?? start;
    setForm((f) => ({ ...f, content: f.content.slice(0, start) + token + f.content.slice(end) }));
  };

  const applyBold = () => {
    const el = contentRef.current;
    const start = el?.selectionStart ?? form.content.length;
    const end = el?.selectionEnd ?? start;
    const selected = form.content.slice(start, end) || "texte en gras";
    setForm((f) => ({ ...f, content: f.content.slice(0, start) + `**${selected}**` + f.content.slice(end) }));
  };

  const addListItem = () => {
    setForm((f) => ({ ...f, content: `${f.content}${f.content && !f.content.endsWith("\n") ? "\n" : ""}- ` }));
    contentRef.current?.focus();
  };

  const uploadImages = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const body = new FormData();
        body.append("file", file);
        const { data } = await api.post("/admin/upload", body, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((f) => ({ ...f, images: [...f.images, data.url] }));
      }
      toast.success("Image(s) ajoutée(s)");
    } catch (error) {
      toast.error(apiError(error, "Upload impossible"));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  useEffect(() => {
    const path = order?.payment_claim?.proof_path;
    if (!path) return undefined;
    let active = true;
    let url = "";
    api.get(`/admin/orders/${id}/payment-proof`, { responseType: "blob" }).then(({ data }) => {
      if (!active) return;
      url = URL.createObjectURL(data);
      setProofUrl(url);
    }).catch(() => {});
    return () => {
      active = false;
      if (url) URL.revokeObjectURL(url);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, order?.payment_claim?.proof_path]);

  const verifyPayment = async (action) => {
    if (action === "reject" && !verifyNote.trim() &&
        !window.confirm("Rejeter le paiement sans indiquer de motif ?")) return;
    if (action === "confirm" &&
        !window.confirm(`Confirmer le paiement de ${order.total} FCFA ? La commande passera en traitement.`)) return;
    setVerifying(true);
    try {
      const { data } = await api.post(`/admin/orders/${id}/payment/verify`, { action, note: verifyNote.trim() });
      setOrder(data);
      setMeta((m) => ({ ...m, status: data.status }));
      setVerifyNote("");
      toast.success(action === "confirm" ? "🟢 Paiement confirmé — commande en traitement" : "🔴 Paiement rejeté");
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setVerifying(false);
    }
  };

  const uploadAttachments = async (list) => {
    if (!list?.length) return;
    setUploadingFile(true);
    try {
      for (const file of Array.from(list)) {
        const body = new FormData();
        body.append("file", file);
        const { data } = await api.post("/admin/upload", body, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((f) => ({ ...f, files: [...f.files, { url: data.url, name: file.name, size: data.size, ext: data.ext }] }));
      }
      toast.success("Fichier(s) joint(s) ajouté(s)");
    } catch (error) {
      toast.error(apiError(error, "Upload impossible"));
    } finally {
      setUploadingFile(false);
      if (attachRef.current) attachRef.current.value = "";
    }
  };

  const moveImage = (index, dir) => {
    setForm((f) => {
      const images = [...f.images];
      const target = index + dir;
      if (target < 0 || target >= images.length) return f;
      [images[index], images[target]] = [images[target], images[index]];
      return { ...f, images };
    });
  };

  const saveMeta = async () => {
    setSavingMeta(true);
    try {
      const { data } = await api.put(`/admin/orders/${id}`, meta);
      setOrder((o) => ({ ...o, ...data, account: o?.account }));
      setMeta((m) => ({ ...m, note: "" }));
      toast.success("Statut mis à jour — le client est notifié");
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setSavingMeta(false);
    }
  };

  const markPaid = async () => {
    try {
      const { data } = await api.put(`/admin/orders/${id}`, { payment_status: "paid" });
      setOrder((o) => ({ ...o, ...data, account: o?.account }));
      toast.success("Paiement marqué comme reçu");
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  const assignKeys = async () => {
    setAssigning(true);
    try {
      const { data } = await api.post(`/admin/orders/${id}/assign-keys`);
      setOrder((o) => ({ ...o, ...data, account: o?.account }));
      const r = data.assign_result || {};
      if (r.missing) toast.warning(`${r.assigned} clé(s) attribuée(s) — ${r.missing} manquante(s) (stock épuisé)`);
      else toast.success(`${r.assigned} clé(s) attribuée(s) depuis le stock`);
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setAssigning(false);
    }
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/orders/${id}/delivery`, { ...form });
      setOrder((o) => ({ ...o, ...data, account: o?.account }));
      toast.success("Brouillon enregistré — invisible pour le client");
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setSaving(false);
    }
  };

  const deliver = async () => {
    if (!window.confirm("Livrer la commande ? Le client verra immédiatement ce contenu dans « Mes commandes ».")) return;
    setDelivering(true);
    try {
      const { data } = await api.post(`/admin/orders/${id}/deliver`, { ...form });
      setOrder((o) => ({ ...o, ...data, account: o?.account }));
      setMeta((m) => ({ ...m, status: data.status || "delivered", tracking_number: data.tracking_number || m.tracking_number }));
      toast.success("Commande livrée — le client peut maintenant voir la livraison");
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setDelivering(false);
    }
  };

  if (!order) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  const status = ORDER_STATUS[order.status] || ORDER_STATUS.processing;
  const delivered = order.delivery?.status === "delivered";
  const allFields = (order.licenses || []).filter((l) => (l.fields || []).length > 0);

  return (
    <div className="space-y-5" data-testid="admin-order-detail">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to="/admin/commandes" data-testid="back-to-orders"
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-line text-slate-500 hover:border-brand hover:text-brand">
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div>
            <h1 className="font-display text-xl font-extrabold text-ink">
              Gestion de la commande {order.order_number}
            </h1>
            <p className="text-xs text-slate-500">
              Commandée le {formatDateTime(order.created_at)} via {PAYMENT_LABELS[order.payment_method] || order.payment_method}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className={`rounded-full border px-3 py-1.5 text-xs font-bold ${status.className}`} data-testid="order-detail-status-badge">
            {status.label}
          </span>
          <span data-testid="order-detail-status"
                className={`rounded-full border px-3.5 py-1.5 text-xs font-bold ${delivered ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}>
            {delivered ? "Livraison envoyée au client" : order.delivery?.status === "draft" ? "Brouillon enregistré — en attente de livraison" : "En attente de livraison"}
          </span>
          {order.reception_confirmed && (
            <span data-testid="admin-reception-confirmed"
                  className="flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-700">
              <PackageCheck className="h-3.5 w-3.5" /> Réception confirmée par le client
            </span>
          )}
        </div>
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,320px)_minmax(0,1fr)_minmax(0,340px)]">
        {/* -------- colonne gauche -------- */}
        <div className="space-y-4">
          <Section icon={User} title="Informations client">
            <Row label="Nom" value={order.customer?.name} />
            <Row label="Email" value={order.customer?.email} />
            <Row label="Téléphone" value={order.customer?.phone ? <WhatsAppLink phone={order.customer.phone} /> : null} />
            <Row label="Pays" value={order.customer?.country} />
            {order.account && (
              <>
                <Row label="Compte" value={order.account.email} />
                <Row label="Inscrit le" value={formatDateTime(order.account.created_at)} />
              </>
            )}
            {order.notes && <Row label="Note client" value={order.notes} />}
            {order.reception_confirmed && (
              <Row label="Réception client" value={`Confirmée le ${formatDateTime(order.reception_confirmed_at)}`} />
            )}
            {order.reception_comment && <Row label="Commentaire réception" value={order.reception_comment} />}
          </Section>

          <Section icon={Truck} title="Statut & suivi">
            <label className="block">
              <span className="mb-1 block text-[10px] font-bold uppercase text-slate-400">Statut de la commande</span>
              <select
                data-testid="order-status-select"
                value={meta.status}
                onChange={(e) => setMeta((m) => ({ ...m, status: e.target.value }))}
                className="h-10 w-full rounded-lg border border-line px-2 text-xs font-semibold focus:border-brand focus:outline-none"
              >
                {ORDER_STATUS_OPTIONS.map((value) => (
                  <option key={value} value={value}>{ORDER_STATUS[value].label}</option>
                ))}
                {order.status === "completed" && <option value="completed">Livrée (ancien)</option>}
              </select>
            </label>
            <label className="mt-2 block">
              <span className="mb-1 block text-[10px] font-bold uppercase text-slate-400">Numéro de suivi (généré automatiquement)</span>
              <input
                data-testid="order-tracking-input"
                value={meta.tracking_number}
                onChange={(e) => setMeta((m) => ({ ...m, tracking_number: e.target.value }))}
                placeholder="Ex : DHL123456789"
                className="h-10 w-full rounded-lg border border-line px-3 text-xs focus:border-brand focus:outline-none"
              />
            </label>
            <label className="mt-2 block">
              <span className="mb-1 block text-[10px] font-bold uppercase text-slate-400">Note (visible dans l'historique)</span>
              <input
                data-testid="order-status-note"
                value={meta.note}
                onChange={(e) => setMeta((m) => ({ ...m, note: e.target.value }))}
                placeholder="Ex : Colis remis au transporteur"
                className="h-10 w-full rounded-lg border border-line px-3 text-xs focus:border-brand focus:outline-none"
              />
            </label>
            <button
              data-testid="order-meta-save"
              onClick={saveMeta}
              disabled={savingMeta}
              className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg bg-ink px-3 py-2.5 text-xs font-bold text-white hover:bg-brand disabled:opacity-60"
            >
              {savingMeta ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Truck className="h-3.5 w-3.5" />}
              Mettre à jour le statut
            </button>
          </Section>

          <Section icon={CreditCard} title="Informations de paiement">
            <Row label="Méthode" value={PAYMENT_LABELS[order.payment_method] || order.payment_method} />
            {order.transaction_id && <Row label="Transaction" value={order.transaction_id} />}
            <Row label="Montant total" value={<span className="font-black text-brand">{formatPrice(order.total, "XOF")}</span>} />
            <Row label="Sous-total" value={formatPrice(order.subtotal, "XOF")} />
            {order.discount > 0 && <Row label="Réduction" value={`- ${formatPrice(order.discount, "XOF")}`} />}
            {order.fees > 0 && <Row label="Frais" value={formatPrice(order.fees, "XOF")} />}
            <Row label="Statut paiement" value={
              <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${(PAYMENT_STATUS[order.payment_status] || PAYMENT_STATUS.pending).className}`}>
                {(PAYMENT_STATUS[order.payment_status] || PAYMENT_STATUS.pending).label}
              </span>
            } />
            {order.payment_status !== "paid" && !order.payment_claim && (
              <button
                data-testid="mark-paid-btn"
                onClick={markPaid}
                className="mt-2 w-full rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-700 hover:bg-emerald-100"
              >
                ✓ Marquer le paiement comme reçu
              </button>
            )}
          </Section>

          {order.payment_claim && (
            <Section icon={CreditCard} title="💳 Vérification du paiement">
              <div
                data-testid="payment-claim-status"
                className={`mb-3 rounded-lg border px-3 py-2 text-[11px] font-bold ${
                  order.payment_claim.status === "confirmed"
                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                    : order.payment_claim.status === "rejected"
                      ? "border-rose-200 bg-rose-50 text-rose-700"
                      : "border-orange-200 bg-orange-50 text-orange-700"
                }`}
              >
                {order.payment_claim.status === "confirmed"
                  ? "🟢 Paiement confirmé"
                  : order.payment_claim.status === "rejected"
                    ? "🔴 Paiement rejeté"
                    : "🟠 Paiement à vérifier — le client a déclaré avoir payé"}
              </div>
              <Row label="Moyen choisi" value={order.payment_claim.method_name} />
              <Row
                label="Montant attendu"
                value={<span className="font-black text-brand">{formatPrice(order.payment_claim.amount_expected ?? order.total, "XOF")}</span>}
              />
              <Row label="Numéro utilisé" value={order.payment_claim.phone ? <WhatsAppLink phone={order.payment_claim.phone} /> : null} />
              <Row label="ID de transaction" value={<span className="font-mono">{order.payment_claim.transaction_id}</span>} />
              <Row label="Déclaré le" value={formatDateTime(order.payment_claim.submitted_at)} />
              {order.payment_claim.verified_at && (
                <Row label="Vérifié par" value={`${order.payment_claim.verified_by} · ${formatDateTime(order.payment_claim.verified_at)}`} />
              )}
              {order.payment_claim.admin_note && <Row label="Commentaire admin" value={order.payment_claim.admin_note} />}
              {order.payment_claim.rejection_reason && (
                <Row label="Motif du rejet" value={order.payment_claim.rejection_reason} />
              )}

              <div className="mt-3">
                <span className="mb-1.5 block text-[10px] font-bold uppercase text-slate-400">🖼️ Preuve de paiement</span>
                {order.payment_claim.proof_path ? (
                  proofUrl ? (
                    <a href={proofUrl} target="_blank" rel="noreferrer" data-testid="payment-proof-image-link">
                      <img
                        src={proofUrl}
                        alt="Preuve de paiement"
                        data-testid="payment-proof-image"
                        className="max-h-64 w-full rounded-xl border border-line object-contain transition-transform duration-200 hover:scale-[1.01]"
                      />
                    </a>
                  ) : (
                    <div className="flex h-24 items-center justify-center rounded-xl border border-line bg-surface">
                      <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                    </div>
                  )
                ) : (
                  <p className="rounded-xl border border-dashed border-line bg-surface p-3 text-center text-xs text-slate-400" data-testid="no-payment-proof">
                    Aucune preuve de paiement fournie.
                  </p>
                )}
              </div>

              {order.payment_claim.status === "review" && (
                order.managed_by === "vendor" ? (
                  <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs font-bold text-amber-800" data-testid="vendor-managed-banner">
                    🔒 Commande revendeur : la validation du paiement est gérée par le revendeur.
                    Consultez la fiche complète dans « Ventes revendeurs ».
                  </div>
                ) : (
                <div className="mt-4 border-t border-line pt-3">
                  <textarea
                    data-testid="payment-verify-note"
                    rows={2}
                    value={verifyNote}
                    onChange={(e) => setVerifyNote(e.target.value)}
                    placeholder="Commentaire ou motif de rejet (ex : ID de transaction incorrect)…"
                    className="w-full rounded-lg border border-line p-3 text-xs focus:border-brand focus:outline-none"
                  />
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <button
                      data-testid="confirm-payment-btn"
                      onClick={() => verifyPayment("confirm")}
                      disabled={verifying}
                      className="flex items-center justify-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2.5 text-xs font-bold text-white hover:bg-emerald-700 disabled:opacity-60"
                    >
                      {verifying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                      Confirmer le paiement
                    </button>
                    <button
                      data-testid="reject-payment-btn"
                      onClick={() => verifyPayment("reject")}
                      disabled={verifying}
                      className="flex items-center justify-center gap-1.5 rounded-lg bg-rose-600 px-3 py-2.5 text-xs font-bold text-white hover:bg-rose-700 disabled:opacity-60"
                    >
                      <XCircle className="h-3.5 w-3.5" /> Rejeter le paiement
                    </button>
                  </div>
                </div>
                )
              )}
            </Section>
          )}

          <Section icon={Package} title="Produits commandés">
            {(order.items || []).map((item) => (
              <div key={item.product_id} className="flex items-center gap-3 border-b border-line py-2.5 last:border-0">
                <img src={item.image_url} alt="" className="h-10 w-10 rounded-lg border border-line object-contain p-1" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-bold text-ink">{item.name}</p>
                  <p className="text-[11px] text-slate-500">Quantité : {item.quantity} · {formatPrice(item.unit_price, "XOF")}</p>
                </div>
                <span className="text-xs font-bold text-ink">{formatPrice(item.line_total, "XOF")}</span>
              </div>
            ))}
          </Section>

          <Section icon={ClipboardList} title="Champs renseignés par le client">
            {allFields.length === 0 ? (
              <p className="text-xs text-slate-400">Aucune information supplémentaire saisie.</p>
            ) : (
              allFields.map((entry, i) => (
                <div key={i} className="border-b border-line py-2 last:border-0" data-testid={`order-fields-block-${i}`}>
                  {allFields.length > 1 && <p className="mb-1 text-[10px] font-bold uppercase text-slate-400">{entry.product_name}</p>}
                  {entry.fields.map((field) => (
                    <Row key={field.key} label={field.label} value={field.value} />
                  ))}
                </div>
              ))
            )}
          </Section>

          {(order.status_history || []).length > 0 && (
            <Section icon={History} title="Historique de la commande">
              <ol className="space-y-2.5" data-testid="order-history-timeline">
                {[...order.status_history].reverse().map((h, i) => (
                  <li key={i} className="flex gap-2.5 text-xs">
                    <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-brand" />
                    <div className="min-w-0">
                      <p className="font-bold text-ink">{h.label || h.status}</p>
                      {h.note && <p className="text-slate-500">{h.note}</p>}
                      <p className="text-[10px] text-slate-400">
                        {formatDateTime(h.at)}{h.by && h.by !== "system" ? ` · ${h.by}` : ""}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </Section>
          )}
        </div>

        {/* -------- colonne centrale : éditeur -------- */}
        <div className="rounded-2xl border border-line bg-white p-5">
          <p className="flex items-center gap-2 text-sm font-extrabold text-ink">
            <PenLine className="h-4 w-4 text-brand" /> Réponse / Livraison manuelle
          </p>
          <p className="mt-0.5 text-[11px] text-slate-500">Rédigez les informations que le client verra après livraison.</p>

          <label className="mt-4 block">
            <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-500">
              Titre de la livraison (visible par le client)
            </span>
            <input
              data-testid="delivery-title"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="Ex : Votre commande a été traitée"
              className="h-11 w-full rounded-xl border border-line px-4 text-sm focus:border-brand focus:outline-none focus:ring-4 focus:ring-brand/10"
            />
          </label>

          <div className="mt-4">
            <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-500">Contenu de la livraison</span>
            <div className="flex flex-wrap items-center gap-1.5 rounded-t-xl border border-b-0 border-line bg-surface px-2 py-1.5">
              <button type="button" data-testid="editor-bold" onClick={applyBold} title="Gras (**texte**)"
                      className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-600 hover:bg-white hover:text-ink">
                <Bold className="h-4 w-4" />
              </button>
              <button type="button" data-testid="editor-list" onClick={addListItem} title="Liste à puces"
                      className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-600 hover:bg-white hover:text-ink">
                <List className="h-4 w-4" />
              </button>
              <span className="text-[10px] text-slate-400">**gras** · - liste · # titre</span>
            </div>
            <textarea
              ref={contentRef}
              data-testid="delivery-content"
              rows={10}
              value={form.content}
              onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
              placeholder={"Bonjour,\n\nMerci pour votre achat.\nVotre commande est maintenant prête.\n\n- Clé : XXXXX-XXXXX\n- Identifiant : ...\n"}
              className="w-full rounded-b-xl border border-line p-4 text-sm leading-relaxed focus:border-brand focus:outline-none focus:ring-4 focus:ring-brand/10"
            />
            <div className="flex flex-wrap gap-4">
              <LinkInserter testId="delivery" onInsert={(token) => insertAtCursor(token)} />
            </div>
          </div>

          <div className="mt-4">
            <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-wide text-slate-500">
              Images jointes (visibles par le client)
            </span>
            <div className="flex flex-wrap gap-2.5">
              {form.images.map((img, index) => (
                <div key={img} className="group relative h-24 w-24 overflow-hidden rounded-xl border border-line bg-slate-50">
                  <img src={fileUrl(img)} alt="" className="h-full w-full object-cover" />
                  <div className="absolute inset-x-0 bottom-0 flex justify-center gap-1 bg-white/90 p-1 opacity-0 transition-opacity group-hover:opacity-100">
                    <button type="button" onClick={() => moveImage(index, -1)} data-testid={`delivery-img-left-${index}`}
                            className="rounded p-0.5 text-slate-500 hover:text-brand"><MoveLeft className="h-3.5 w-3.5" /></button>
                    <button type="button" data-testid={`delivery-img-remove-${index}`}
                            onClick={() => setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== index) }))}
                            className="rounded p-0.5 text-slate-500 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
                    <button type="button" onClick={() => moveImage(index, 1)} data-testid={`delivery-img-right-${index}`}
                            className="rounded p-0.5 text-slate-500 hover:text-brand"><MoveRight className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
              ))}
              <input ref={fileRef} type="file" accept="image/*" multiple data-testid="delivery-images-input"
                     onChange={(e) => uploadImages(e.target.files)} className="hidden" />
              <button type="button" data-testid="delivery-add-image" onClick={() => fileRef.current?.click()} disabled={uploading}
                      className="flex h-24 w-24 flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed border-line text-slate-400 hover:border-brand hover:text-brand disabled:opacity-60">
                {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
                <span className="text-[10px] font-bold">Ajouter</span>
              </button>
            </div>
          </div>

          <div className="mt-4">
            <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-wide text-slate-500">
              Fichiers joints — PDF & documents (visibles par le client)
            </span>
            <div className="space-y-2">
              {form.files.map((f, index) => (
                <div key={`${f.url}-${index}`} data-testid={`delivery-attach-row-${index}`}
                     className="flex items-center gap-3 rounded-xl border border-line bg-surface p-2.5">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-light text-brand">
                    <FileText className="h-4 w-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-xs font-bold text-ink">{f.name}</span>
                    <span className="block text-[10px] uppercase text-slate-400">
                      {f.ext || "fichier"}{f.size ? ` · ${Math.max(1, Math.round(f.size / 1024))} Ko` : ""}
                    </span>
                  </span>
                  <button type="button" data-testid={`delivery-attach-remove-${index}`}
                          onClick={() => setForm((fm) => ({ ...fm, files: fm.files.filter((_, i) => i !== index) }))}
                          className="rounded p-1 text-slate-400 hover:text-rose-600">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
            <input ref={attachRef} type="file" multiple data-testid="delivery-files-input"
                   accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.rar,.7z,.txt,.csv"
                   onChange={(e) => uploadAttachments(e.target.files)} className="hidden" />
            <button type="button" data-testid="delivery-add-file" onClick={() => attachRef.current?.click()} disabled={uploadingFile}
                    className="mt-2 flex items-center gap-2 rounded-xl border-2 border-dashed border-line px-4 py-2.5 text-xs font-bold text-slate-500 hover:border-brand hover:text-brand disabled:opacity-60">
              {uploadingFile ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />}
              Joindre un fichier (PDF, Word, Excel, ZIP…)
            </button>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-line pt-4">
            <button
              data-testid="save-draft-btn"
              onClick={saveDraft}
              disabled={saving}
              className="flex items-center gap-2 rounded-xl border border-line px-5 py-3 text-sm font-bold text-ink hover:border-brand hover:text-brand disabled:opacity-60"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Enregistrer le brouillon
            </button>
            <button
              data-testid="deliver-order-btn"
              onClick={deliver}
              disabled={delivering}
              className="flex items-center gap-2 rounded-xl bg-emerald-600 px-6 py-3 text-sm font-bold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-60"
            >
              {delivering ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {delivered ? "Mettre à jour la livraison" : "Livrer la commande"}
            </button>
          </div>
          <p className="mt-2 text-[10.5px] text-slate-400">🔒 Le client verra le contenu uniquement après livraison.</p>
        </div>

        {/* -------- colonne droite : aperçu -------- */}
        <div className="space-y-3">
          <p className="flex items-center gap-2 text-sm font-extrabold text-ink">
            <Eye className="h-4 w-4 text-brand" /> Aperçu côté client <span className="text-[10px] font-semibold text-slate-400">(après livraison)</span>
          </p>
          <DeliveryPreview order={order} form={form} />
          {(order.licenses || []).some((l) => l.delivery_mode === "license_key") && (
            <div className="rounded-xl border border-line bg-white p-4" data-testid="license-keys-panel">
              <div className="flex items-center justify-between gap-2">
                <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Clés licence</p>
                {(order.licenses || []).some((l) => l.delivery_mode === "license_key" && !l.key_value) && (
                  <button
                    data-testid="assign-keys-btn"
                    onClick={assignKeys}
                    disabled={assigning}
                    className="flex items-center gap-1.5 rounded-lg border border-brand px-2.5 py-1.5 text-[10px] font-bold text-brand hover:bg-brand-soft disabled:opacity-60"
                  >
                    {assigning ? <Loader2 className="h-3 w-3 animate-spin" /> : <KeyRound className="h-3 w-3" />}
                    Attribuer depuis le stock
                  </button>
                )}
              </div>
              {(order.licenses || []).filter((l) => l.delivery_mode === "license_key").map((l, i) => (
                <p key={i} className="mt-1.5 truncate font-mono text-[11px] text-slate-600" data-testid={`assigned-key-${i}`}>
                  {l.product_name} : {l.key_value || <span className="font-sans text-amber-600">aucune clé attribuée</span>}
                </p>
              ))}
              <p className="mt-2 text-[10px] leading-snug text-slate-400">
                Les clés attribuées restent invisibles pour le client jusqu'à la livraison.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
